import React, { useEffect, useRef, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { useContent } from '../../lib/content';
import { pickLocale } from '../../lib/api';

/**
 * HowItWorks — V2 (refuerzo). The pipeline as a cinematic sequence.
 *
 *  - Each step occupies a 12-col grid with the BIG step number on one side
 *    and a media tile (video or image) floating with parallax on the other.
 *  - Tiles drift up/down based on scroll progress (different speeds → depth).
 *  - SVG vertical "spine" path on the desktop center connects all steps and
 *    animates its strokeDashoffset as you scroll (Awwwards classic).
 *  - Each tile picks an asset from the looks pool (videos when available,
 *    BR after-images otherwise) so the section becomes a media reel as well.
 */

// Map each step index → media (video preferred when available)
const STEP_MEDIA = [
  { kind: 'video', id: 'V01' },
  { kind: 'image', id: '03' },   // BR03 (after)
  { kind: 'video', id: 'V08' },
  { kind: 'image', id: '07' },   // BR07
  { kind: 'video', id: 'V11' },
  { kind: 'image', id: '13' },   // BR13
];

function FloatingMedia({ media, index }) {
  const wrapperRef = useRef(null);
  const videoRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: wrapperRef,
    offset: ['start end', 'end start'],
  });
  const drift = useTransform(scrollYProgress, [0, 1], ['8%', '-8%']);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.96, 1, 0.96]);

  useEffect(() => {
    if (media.kind !== 'video' || !videoRef.current) return;
    const v = videoRef.current;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) v.play().catch(() => {});
        else v.pause();
      });
    }, { threshold: 0.35 });
    obs.observe(v);
    return () => obs.disconnect();
  }, [media.kind]);

  const tilt = index % 2 === 0 ? -1.2 : 1.2;

  return (
    <motion.div
      ref={wrapperRef}
      style={{ y: drift, scale, rotate: tilt }}
      className="relative aspect-[9/16] w-full max-w-[320px] mx-auto md:mx-0 overflow-hidden border border-line bg-bg-secondary shadow-[0_24px_60px_rgba(0,0,0,0.45)]"
      data-testid={`how-media-${index}`}
    >
      {media.kind === 'video' ? (
        <video
          ref={videoRef}
          src={`/uploads/looks/video/${media.id}.mp4`}
          poster={`/uploads/looks/video/${media.id}-poster.webp`}
          muted
          loop
          playsInline
          preload="metadata"
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        <img
          src={`/uploads/looks/after/${media.id}-medium.webp`}
          srcSet={`/uploads/looks/after/${media.id}-thumb.webp 500w, /uploads/looks/after/${media.id}-medium.webp 1000w, /uploads/looks/after/${media.id}-full.webp 1600w`}
          sizes="(min-width: 1024px) 33vw, 90vw"
          alt=""
          loading="lazy"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}
      <div className="absolute top-3 left-3 font-mono text-[10px] uppercase tracking-[0.22em] text-ink bg-bg/70 backdrop-blur-sm border border-line-strong px-2 py-1">
        {media.kind === 'video' ? 'CLIP' : 'STILL'} · {media.id}
      </div>
    </motion.div>
  );
}

function Spine() {
  // Animated vertical path that draws as the section scrolls
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start 0.7', 'end 0.3'],
  });
  const dash = useTransform(scrollYProgress, [0, 1], [1, 0]);
  return (
    <div ref={ref} aria-hidden className="absolute left-1/2 top-0 bottom-0 -translate-x-1/2 hidden lg:block pointer-events-none" data-testid="how-spine">
      <svg viewBox="0 0 2 1000" preserveAspectRatio="none" className="w-px h-full">
        <motion.path
          d="M 1 0 L 1 1000"
          stroke="var(--accent)"
          strokeWidth="2"
          strokeDasharray="1"
          pathLength="1"
          style={{ strokeDashoffset: dash }}
          fill="none"
          opacity="0.55"
        />
      </svg>
    </div>
  );
}

export default function HowItWorks() {
  const { content, locale } = useContent();
  const data = content.how || {};
  const steps = data.steps || [];

  return (
    <section
      id="how"
      data-testid="how-section"
      className="section bg-bg overflow-hidden relative"
    >
      <div className="container-x relative">
        <Spine />

        <div className="mb-14 md:mb-24 max-w-3xl">
          <p className="text-eyebrow mb-4">{pickLocale(data.eyebrow, locale)}</p>
          <h2 className="font-display text-4xl md:text-6xl lg:text-7xl tracking-tight leading-[0.95]" data-testid="how-title">
            {pickLocale(data.title, locale)}
          </h2>
          {data.lead && (
            <p className="text-ink-muted text-base md:text-lg leading-relaxed mt-6 max-w-xl">
              {pickLocale(data.lead, locale)}
            </p>
          )}
        </div>

        <div className="space-y-20 md:space-y-32">
          {steps.map((s, i) => {
            const reverse = i % 2 === 1;
            const media = STEP_MEDIA[i] || STEP_MEDIA[i % STEP_MEDIA.length];
            return (
              <motion.div
                key={i}
                data-testid={`how-step-${i}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.7 }}
                className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 items-center"
              >
                {/* Number block */}
                <div className={`md:col-span-3 ${reverse ? 'md:order-3 md:text-right' : 'md:order-1'}`}>
                  <div className="edit-num text-ink/85 leading-none select-none">{s.number}</div>
                  <div className="mt-3 inline-flex items-center gap-2 text-eyebrow text-acid">
                    <span className="block w-8 h-px bg-acid" />
                    {locale === 'es' ? 'PASO' : 'STEP'} {s.number}
                  </div>
                </div>

                {/* Floating media */}
                <div className={`md:col-span-4 ${reverse ? 'md:order-2' : 'md:order-2'} flex justify-center`}>
                  <FloatingMedia media={media} index={i} />
                </div>

                {/* Copy */}
                <div className={`md:col-span-5 ${reverse ? 'md:order-1' : 'md:order-3'}`}>
                  <h3 className="font-display text-3xl md:text-4xl text-ink mb-4 leading-tight">
                    {pickLocale(s.title, locale)}
                  </h3>
                  <p className="text-ink-muted text-base leading-relaxed">
                    {pickLocale(s.description, locale)}
                  </p>
                  {s.bullets && Array.isArray(s.bullets) && s.bullets.length > 0 && (
                    <ul className="mt-5 space-y-2">
                      {s.bullets.map((b, j) => (
                        <li key={j} className="flex items-start gap-2 text-ink-muted text-sm">
                          <ArrowRight size={11} className="text-acid mt-1 shrink-0" />
                          <span>{pickLocale(b, locale)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="mt-6 h-px w-24 bg-acid" />
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
